import React from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const KeywordsSection = ({ section, practice, theme, highlightedField, deviceType = "desktop" }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}

  // Get keywords from practice specialties if not provided in settings
  const getKeywordsFromPractice = () => {
    if (!practice || !practice.skills) return []

    return practice.skills.map((skill, index) => ({
      id: index,
      text: skill.specialty?.name || skill.name,
      popularity: "medium" // Default popularity for practice specialties
    }))
  }

  const keywords = settings.keywords || getKeywordsFromPractice()
  const maxKeywords = settings.maxKeywords || 20
  const displayKeywords = keywords.slice(0, maxKeywords)

  // Split keywords based on device type
  const isMobile = deviceType === "mobile"
  const columnCount = isMobile ? 1 : 3
  const chunkSize = Math.ceil(displayKeywords.length / columnCount)

  const keywordColumns = []
  for (let i = 0; i < columnCount; i++) {
    keywordColumns.push(displayKeywords.slice(i * chunkSize, (i + 1) * chunkSize))
  }

  const handleKeywordClick = (keyword) => {
    if (settings.allowClick === false) return
    // Handle keyword click - could search for related content or services
    console.log("Keyword clicked:", keyword.text) // eslint-disable-line no-console
  }

  const renderKeywordItem = (keyword) => (
    <div
      key={keyword.id}
      className={twMerge(
        "flex w-full items-center gap-[8px]",
        settings.allowClick !== false ? "cursor-pointer" : "cursor-default"
      )}
      onClick={() => handleKeywordClick(keyword)}>
      {/* Checkmark icon */}
      <div className="h-4 w-4 shrink-0">
        <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-full w-full">
          <path
            d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"
            fill={theme?.colors?.primary || "#bd654f"}
          />
        </svg>
      </div>

      {/* Keyword text */}
      <span
        className="text-[16px] leading-[24px]"
        style={{
          fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
          color: theme?.colors?.textDark || "#55433f"
        }}>
        {keyword.text}
      </span>
    </div>
  )

  const renderKeywordColumn = (columnKeywords, columnIndex) => (
    <div
      key={columnIndex}
      className={`flex flex-col items-start justify-start gap-[12px] ${isMobile ? "w-full" : "flex-1"}`}>
      {columnKeywords.map(renderKeywordItem)}
    </div>
  )

  const renderContent = () => (
    <div className="flex flex-col items-center justify-start gap-[40px]">
      {/* Headline */}
      <div className="text-center">
        <h2
          className="text-[40px] font-bold leading-[48px]"
          style={{
            fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
            fontWeight: theme?.fonts?.headingWeight || "700",
            color: theme?.colors?.textDark || "#55433f"
          }}>
          I help with
        </h2>
      </div>

      {/* Keywords box */}
      {displayKeywords.length > 0 && (
        <div
          className={`flex w-full flex-row items-start justify-start ${isMobile ? "gap-0" : "gap-[32px]"} ${
            isMobile ? "p-6" : "p-[56px]"
          }`}
          style={{
            backgroundColor: theme?.colors?.bgExtraLight || "#f8f6f5"
          }}>
          {keywordColumns.map((columnKeywords, index) => renderKeywordColumn(columnKeywords, index))}
        </div>
      )}
    </div>
  )

  return (
    <section
      className={twMerge("py-24", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
      style={{
        backgroundColor: theme?.colors?.white || "#ffffff"
      }}>
      <AnimatedSection className="mx-auto w-full px-4 md:max-w-[1160px] md:px-0">{renderContent()}</AnimatedSection>
    </section>
  )
}

export default KeywordsSection
